/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Box, Typography } from "@material-ui/core";
import {
  DsmEmptyState,
  DsmGrid,
  DsmIcon,
} from "@dsm-dcs/design-system-react";
import DatasetListTableV2 from "./DatasetListTableV2";
import Can from "../../../modules/Auth/Can";
import { DatasetType, FormType, isReadOnlyAnimalSystem } from "./common";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import { prodProcessDatasetsPageStyles } from "../../../ThemeProvider";
import GuardedButton from "../helpers/GuardedButton";

interface BaselineSectionProps {
  prodProcess: any;
  activeBaseline: any;
  benchmark: boolean;
  // When true, the farm's data was ingested via the partner API (read-only in UI).
  partnerReadOnly?: boolean;
  customerID: string;
  farmID: string;
  processID: string;
  selectedFarmName: string;
  openDataset: (
    datasetType: string,
    baselineId: string,
    formType: string,
  ) => void;
  makeDeleteHandler: (type: string) => (itemName: string) => void;
  handleDuplicate: (datasetType: string, item: any) => void;
}

const BaselineSection = ({
  prodProcess,
  activeBaseline,
  benchmark,
  partnerReadOnly = false,
  customerID,
  farmID,
  processID,
  selectedFarmName,
  openDataset,
  makeDeleteHandler,
  handleDuplicate,
}: BaselineSectionProps) => {
  const intl = useIntl();
  const pageClasses = prodProcessDatasetsPageStyles();

  return (
    <>
      <DsmGrid
        className={`${pageClasses.sectionGrid} ${
          activeBaseline
            ? pageClasses.baselineGridTemplateWithActive
            : pageClasses.baselineGridTemplate
        }`}
      >
        <Box className={pageClasses.alignSelfStart}>
          <Typography
            variant="h6"
            className={pageClasses.datasetsTableIntroTitle}
          >
            {intl.formatMessage({ id: "SUSTELL.CURRENT.BASELINE.DATASET" })}
          </Typography>

          <Typography
            variant="body1"
            className={pageClasses.bodyTextNeutralDarker}
          >
            {intl.formatMessage({
              id: "SUSTELL.CURRENT.BASELINE.DATASET.DESCRIPTION.1",
            })}
          </Typography>

          <Typography
            variant="body1"
            className={pageClasses.bodyTextNeutralDarker}
          >
            {intl.formatMessage({
              id: "SUSTELL.CURRENT.BASELINE.DATASET.DESCRIPTION.2",
            })}
          </Typography>
        </Box>

        {Can("update", "Baseline") &&
          !benchmark &&
          !isReadOnlyAnimalSystem(prodProcess.animalType) &&
          activeBaseline && (
            <Box display="ruby">
              <GuardedButton
                className={pageClasses.editBaselineButton}
                onClick={() =>
                  openDataset(
                    DatasetType.BASELINE,
                    activeBaseline.id,
                    FormType.Edit,
                  )
                }
                locked={partnerReadOnly}
                lockedReason={intl.formatMessage({
                  id: "SUSTELL.FARM.PARTNER_INGESTED.READONLY",
                })}
                variant="secondary"
              >
                <DsmIcon slot="before" name="general/edit-01" />
                {intl.formatMessage({ id: "SUSTELL.EDIT_BASELINE" })}
              </GuardedButton>

              <Typography className={pageClasses.hintText}>
                {intl.formatMessage({
                  id: "SUSTELL.CURRENT.BASELINE.DATASET.EDIT.HINT",
                })}
              </Typography>
            </Box>
          )}

        {Can("create", "Baseline") &&
          !benchmark &&
          !isReadOnlyAnimalSystem(prodProcess.animalType) && (
            <Box>
              <GuardedButton
                className={pageClasses.addBaselineButton}
                onClick={() =>
                  openDataset(DatasetType.BASELINE, "NEW", FormType.Add)
                }
                locked={partnerReadOnly}
                lockedReason={intl.formatMessage({
                  id: "SUSTELL.FARM.PARTNER_INGESTED.READONLY",
                })}
                variant="secondary"
              >
                <DsmIcon slot="before" name="general/plus" />
                {intl.formatMessage({ id: "SUSTELL.ADD_BASELINE" })}
              </GuardedButton>

              <Typography className={pageClasses.hintText}>
                {intl.formatMessage({
                  id: "SUSTELL.CURRENT.BASELINE.DATASET.ADD.HINT",
                })}
              </Typography>
            </Box>
          )}
      </DsmGrid>

      {activeBaseline ? (
        <DatasetListTableV2
          datasets={[activeBaseline]}
          customerID={customerID}
          variant="baseline"
          farmID={farmID}
          benchmark={benchmark}
          partnerReadOnly={partnerReadOnly}
          processID={prodProcess?.id}
          farmName={selectedFarmName}
          processName={prodProcess?.name}
          animalType={prodProcess.animalType}
          openDataset={(baselineId, formType) =>
            openDataset(DatasetType.BASELINE, baselineId, formType)
          }
          deleteHandler={makeDeleteHandler(DatasetType.BASELINE)}
          duplicateDataset={(item) =>
            handleDuplicate(DatasetType.BASELINE, item)
          }
          archive={false}
        />
      ) : (
        <DsmEmptyState
          icon="general/search-md"
          header={intl.formatMessage({ id: "GENERAL.NO_DATA" })}
          description={intl.formatMessage({
            id: "SUSTELL.PROCESS.CREATE.BASELINE",
          })}
          className={pageClasses.sectionEmptyState}
        />
      )}
    </>
  );
};

export default BaselineSection;
